import { Card, Select } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { DIMENSION_BY_KEY, MEASURES, MEASURE_BY_KEY, ROOT_KEY } from './constants';
import { INK, INK_FAINT, INK_SOFT, LINE, PASTEL, SEASON_RAMPS } from './pivotStyles';

const TOP_N = 12;
const DELTA_COLOR = '#9DB0C4';

/** Medidas que se pueden graficar: las aditivas (Serie-color no se apila ni se reparte). */
const CHARTABLE = MEASURES.filter((m) => m.key !== 'families');

const val = (node, colKey, m, field = 'value') => Number(node?.cells?.[colKey]?.[m]?.[field]) || 0;

/** Gris pastel de Básico (`SEASON_RAMPS.BASICO[2]`, también en `PASTEL`). */
const BASICO_GREY = SEASON_RAMPS.BASICO[2];

const plain = (t) =>
  String(t || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

/** Temporada de un nodo de Campaña o Temporada: del `meta`, de la clave o, en último caso, de la etiqueta. */
const seasonOf = (g) => {
  const fromMeta = plain(g.meta?.season);
  if (fromMeta) return fromMeta;
  const parts = String(g.key).split('\u001f');
  const fromKey = plain(parts[parts.length - 1]);
  if (SEASON_RAMPS[fromKey]) return fromKey;
  return plain(String(g.label).split(' ')[0]);
};

/** «Verano Ant. 2022»: las campañas de más de cinco años, agrupadas por el back. */
const isOlderBucket = (g) => /\bAnt\./.test(g.label || '');

/**
 * Color por grupo de columna. Con Campaña o Temporada, tono por temporada e
 * intensidad por antigüedad: la más reciente, la más oscura; el cajón
 * «Ant.» de cada temporada, el tono más claro. Básico es el gris pastel.
 */
const colorsFor = (groups, dim) => {
  if (dim === 'campaign' || dim === 'season') {
    const bySeason = {};
    groups.forEach((g) => {
      const season = seasonOf(g);
      const year = Number(g.meta?.year) || Number(String(g.key).split('\u001f')[0]) || 0;
      (bySeason[season] = bySeason[season] || []).push({ key: g.key, year, older: isOlderBucket(g) });
    });
    const out = {};
    Object.entries(bySeason).forEach(([season, list]) => {
      const ramp = SEASON_RAMPS[season] || SEASON_RAMPS.BASICO;
      // Básico arranca en el gris pastel (`#C9CCCF`), no en el gris oscuro.
      const first = SEASON_RAMPS[season] && season !== 'BASICO' ? 0 : 2;
      list
        .filter((x) => !x.older)
        .sort((a, b) => b.year - a.year)
        .forEach((x, i) => {
          out[x.key] = ramp[Math.min(first + i, ramp.length - 1)];
        });
      list
        .filter((x) => x.older)
        .forEach((x) => {
          out[x.key] = ramp[ramp.length - 1];
        });
    });
    return out;
  }
  // Resto de dimensiones (Año incluido, donde el back saca los básicos a un
  // nodo «Básico»): Básico en su gris pastel y los demás en la paleta sin
  // ese gris, para que ningún otro grupo se confunda con él.
  const others = PASTEL.filter((c) => c !== BASICO_GREY);
  let i = 0;
  return groups.reduce((acc, g) => {
    if (plain(g.label) === 'BASICO' || plain(g.meta?.season) === 'BASICO') return { ...acc, [g.key]: BASICO_GREY };
    const color = others[i % others.length];
    i += 1;
    return { ...acc, [g.key]: color };
  }, {});
};

/**
 * Tooltip de las gráficas: un `div` fijo que sigue al ratón. Sin librería.
 * `lines`: [título, ...detalle]. Cerca del borde derecho se abre hacia la
 * izquierda para no salirse de la ventana.
 */
const useTip = () => {
  const [tip, setTip] = useState(null);
  const at = (e) => ({ x: e.clientX, y: e.clientY, flip: e.clientX > window.innerWidth - 280 });
  const bind = (lines) => ({
    onMouseEnter: (e) => setTip({ lines, ...at(e) }),
    onMouseMove: (e) => setTip((t) => (t ? { ...t, ...at(e) } : { lines, ...at(e) })),
    onMouseLeave: () => setTip(null),
  });
  const node = tip ? (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x + (tip.flip ? -14 : 14),
        top: tip.y + 14,
        transform: tip.flip ? 'translateX(-100%)' : 'none',
        zIndex: 600,
        pointerEvents: 'none',
        background: '#fff',
        border: `1px solid ${LINE}`,
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        padding: '6px 10px',
        fontSize: 12,
        lineHeight: '17px',
        color: INK_SOFT,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {tip.lines.filter(Boolean).map((l, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={i === 0 ? { fontWeight: 600, color: INK } : undefined}>
          {l}
        </div>
      ))}
    </div>
  ) : null;
  return { bind, node };
};

const Title = ({ children }) => (
  <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 10 }}>{children}</div>
);
const Note = ({ children }) => <div style={{ fontSize: 12, color: INK_FAINT, marginTop: 10 }}>{children}</div>;

/** Barras apiladas: primer nivel de filas × primer nivel de columnas. */
const StackedBars = ({ pivot, m, fmt, rowDim, colDim, tip }) => {
  const kind = MEASURE_BY_KEY[m].kind;
  const groups = pivot.columns.children || [];
  const stacked = groups.length > 0;
  const colors = useMemo(() => colorsFor(groups, colDim), [groups, colDim]);

  const rows = useMemo(() => {
    const list = (pivot.rows.children || []).map((n) => ({
      key: n.key,
      label: n.label,
      total: val(n, ROOT_KEY, m),
      segs: stacked
        ? groups.map((g) => ({ key: g.key, v: val(n, g.key, m) }))
        : [{ key: ROOT_KEY, v: val(n, ROOT_KEY, m) }],
    }));
    list.sort((a, b) => b.total - a.total);
    if (list.length <= TOP_N) return list;
    const head = list.slice(0, TOP_N - 1);
    const rest = list.slice(TOP_N - 1);
    head.push({
      key: '__rest',
      label: `Resto (${rest.length})`,
      total: rest.reduce((a, r) => a + r.total, 0),
      segs: head[0].segs.map((s, i) => ({ key: s.key, v: rest.reduce((a, r) => a + r.segs[i].v, 0) })),
    });
    return head;
  }, [pivot, m, groups, stacked]);

  // El "Resto" no marca la escala: agrupa muchos y aplastaría a los demás.
  const max = Math.max(
    1,
    ...rows.filter((r) => r.key !== '__rest').map((r) => r.segs.reduce((a, s) => a + Math.max(0, s.v), 0)),
  );
  const rootTotal = val(pivot.rows, ROOT_KEY, m);

  return (
    <div>
      <Title>
        {`${MEASURE_BY_KEY[m].long} por ${DIMENSION_BY_KEY[rowDim]?.label.toLowerCase() || 'fila'}${
          stacked ? ` y ${DIMENSION_BY_KEY[colDim]?.label.toLowerCase()}` : ''
        }`}
      </Title>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(70px, 140px) 1fr auto',
          gap: '6px 10px',
          alignItems: 'center',
        }}
      >
        {rows.map((r) => (
          <div key={r.key} style={{ display: 'contents' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: INK,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={r.label}
            >
              {r.label}
            </div>
            <div
              style={{ display: 'flex', height: 18, background: '#F6F6F7', borderRadius: 3, overflow: 'hidden' }}
              {...(r.key === '__rest'
                ? tip.bind([
                    r.label,
                    fmt.kpi(kind, r.total),
                    rootTotal ? `${fmt.share(r.total, rootTotal)} del total` : '',
                  ])
                : {})}
            >
              {r.key === '__rest' && (
                <span style={{ fontSize: 11, color: INK_FAINT, alignSelf: 'center', paddingLeft: 6 }}>
                  agrupado, fuera de escala
                </span>
              )}
              {r.key !== '__rest' &&
                r.segs.map((s) =>
                  s.v > 0 ? (
                    <div
                      key={s.key}
                      {...tip.bind(
                        stacked
                          ? [
                              r.label,
                              groups.find((g) => g.key === s.key)?.label,
                              fmt.kpi(kind, s.v),
                              r.total ? `${fmt.share(s.v, r.total)} de ${r.label}` : '',
                            ]
                          : [r.label, fmt.kpi(kind, s.v), rootTotal ? `${fmt.share(s.v, rootTotal)} del total` : ''],
                      )}
                      style={{
                        width: `${(s.v / max) * 100}%`,
                        background: stacked ? colors[s.key] : PASTEL[0],
                        minWidth: 1,
                      }}
                    />
                  ) : null,
                )}
            </div>
            <div style={{ fontSize: 12, color: INK_SOFT, whiteSpace: 'nowrap', textAlign: 'right' }}>
              {fmt.cell(kind, r.total)}
              {rootTotal > 0 && r.total / rootTotal < 0.01
                ? ` (${fmt.pctText(r.total / rootTotal).replace(/^\+/, '')})`
                : ''}
            </div>
          </div>
        ))}
      </div>
      {stacked && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 12 }}>
          {groups.map((g) => (
            <span
              key={g.key}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: INK_SOFT }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 2, background: colors[g.key] }} />
              {g.label}
            </span>
          ))}
        </div>
      )}
      {colDim === 'campaign' && (
        <Note>
          {`Tono: temporada (azul invierno, salmón verano, gris básico). Más oscuro, más reciente.${
            groups.some(isOlderBucket) ? ' «Ant.» agrupa las campañas de más de cinco años.' : ''
          }`}
        </Note>
      )}
    </div>
  );
};

/** Donut: reparto del total por el primer nivel de filas. */
const Donut = ({ pivot, m, fmt, rowDim, tip }) => {
  const kind = MEASURE_BY_KEY[m].kind;
  const total = val(pivot.rows, ROOT_KEY, m);
  const slices = useMemo(() => {
    const list = (pivot.rows.children || [])
      .map((n) => ({ key: n.key, label: n.label, v: Math.max(0, val(n, ROOT_KEY, m)) }))
      .sort((a, b) => b.v - a.v);
    if (list.length <= 7) return list;
    const head = list.slice(0, 6);
    head.push({ key: '__rest', label: `Resto (${list.length - 6})`, v: list.slice(6).reduce((a, x) => a + x.v, 0) });
    return head;
  }, [pivot, m]);
  const sum = slices.reduce((a, s) => a + s.v, 0) || 1;

  const R = 70;
  const r = 48;
  let angle = -Math.PI / 2;
  const arcs = slices.map((s, i) => {
    const a0 = angle;
    const a1 = angle + (s.v / sum) * Math.PI * 2;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad, ang) => `${80 + rad * Math.cos(ang)} ${80 + rad * Math.sin(ang)}`;
    const full = s.v / sum > 0.9999;
    const d = full
      ? `M ${p(R, 0)} A ${R} ${R} 0 1 1 ${p(R, Math.PI)} A ${R} ${R} 0 1 1 ${p(R, 0)} M ${p(r, 0)} A ${r} ${r} 0 1 0 ${p(r, Math.PI)} A ${r} ${r} 0 1 0 ${p(r, 0)} Z`
      : `M ${p(R, a0)} A ${R} ${R} 0 ${large} 1 ${p(R, a1)} L ${p(r, a1)} A ${r} ${r} 0 ${large} 0 ${p(r, a0)} Z`;
    return { ...s, d, color: PASTEL[i % PASTEL.length] };
  });

  return (
    <div>
      <Title>{`Reparto del ${MEASURE_BY_KEY[m].label} por ${DIMENSION_BY_KEY[rowDim]?.label.toLowerCase() || 'fila'}`}</Title>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <svg width="160" height="160" viewBox="0 0 160 160" role="img" aria-label="Reparto del total">
          {arcs.map((a) => (
            <path
              key={a.key}
              d={a.d}
              fill={a.color}
              fillRule="evenodd"
              stroke="#fff"
              strokeWidth="1"
              aria-label={`${a.label}: ${fmt.kpi(kind, a.v)}`}
              {...tip.bind([a.label, fmt.kpi(kind, a.v), `${fmt.share(a.v, sum)} del total`])}
            />
          ))}
          <text x="80" y="78" textAnchor="middle" fontSize="14" fontWeight="600" fill={INK}>
            {fmt.cell(kind, total)}
          </text>
          <text x="80" y="95" textAnchor="middle" fontSize="11" fill={INK_SOFT}>
            {`${MEASURE_BY_KEY[m].label} total`}
          </text>
        </svg>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: '4px 8px',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {arcs.map((a) => (
            <div key={a.key} style={{ display: 'contents' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: a.color }} />
              <span
                style={{ fontSize: 12, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {a.label}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: INK, textAlign: 'right' }}>
                {fmt.pctText(a.v / sum).replace(/^\+/, '')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Variación (con comparativa): por primer nivel de columnas, o de filas si no hay columnas. */
const DeltaBars = ({ pivot, m, fmt, colDim, rowDim, compareLabel, tip }) => {
  const kind = MEASURE_BY_KEY[m].kind;
  const byCols = (pivot.columns.children || []).length > 0;
  const items = byCols
    ? pivot.columns.children.map((g) => ({
        key: g.key,
        label: g.label,
        d: val(pivot.rows, g.key, m, 'delta'),
        cell: pivot.rows.cells?.[g.key]?.[m],
      }))
    : (pivot.rows.children || []).map((n) => ({
        key: n.key,
        label: n.label,
        d: val(n, ROOT_KEY, m, 'delta'),
        cell: n.cells?.[ROOT_KEY]?.[m],
      }));
  const tipLines = (x) => {
    const pct = fmt.deltaPct(x.cell);
    return [
      x.label,
      `Variación sobre ${compareLabel}: ${fmt.signedCell(kind, x.d)}`,
      pct === 'nueva' || pct === 'sin base' ? pct : pct ? `${pct} %` : '',
    ];
  };
  const list = items.length > TOP_N ? [...items].sort((a, b) => Math.abs(b.d) - Math.abs(a.d)).slice(0, TOP_N) : items;
  const max = Math.max(1, ...list.map((x) => Math.abs(x.d)));
  const dim = byCols ? colDim : rowDim;

  return (
    <div>
      <Title>{`Variación del ${MEASURE_BY_KEY[m].label} sobre ${compareLabel}, por ${
        DIMENSION_BY_KEY[dim]?.label.toLowerCase() || 'fila'
      }`}</Title>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(70px, 130px) 1fr 1fr auto',
          gap: '6px 0',
          alignItems: 'center',
        }}
      >
        {list.map((x) => (
          <div key={x.key} style={{ display: 'contents' }}>
            <span
              style={{
                fontSize: 12,
                color: INK,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                paddingRight: 8,
              }}
              title={x.label}
            >
              {x.label}
            </span>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', borderRight: `1px solid ${INK_FAINT}`, height: 16 }}
            >
              {x.d < 0 && (
                <div
                  {...tip.bind(tipLines(x))}
                  style={{ width: `${(-x.d / max) * 100}%`, background: DELTA_COLOR, minWidth: 1 }}
                />
              )}
            </div>
            <div style={{ display: 'flex', height: 16 }}>
              {x.d > 0 && (
                <div
                  {...tip.bind(tipLines(x))}
                  style={{ width: `${(x.d / max) * 100}%`, background: DELTA_COLOR, minWidth: 1 }}
                />
              )}
            </div>
            <span style={{ fontSize: 12, color: INK_SOFT, whiteSpace: 'nowrap', paddingLeft: 8, textAlign: 'right' }}>
              {fmt.signedCell(kind, x.d)}
            </span>
          </div>
        ))}
      </div>
      {items.length > TOP_N && <Note>{`Las ${TOP_N} variaciones más grandes de ${items.length}.`}</Note>}
      <Note>Sin semáforo: más stock no es mejor ni peor. El signo lo dan el lado del eje y la cifra.</Note>
    </div>
  );
};

export const StocksCharts = ({ pivot, measure, onMeasure, fmt, rowDims, colDims, compare, compareLabel }) => {
  const tip = useTip();
  if (!pivot || !pivot.rows?.children?.length) return null;
  const m = CHARTABLE.some((x) => x.key === measure) ? measure : 'pvf';
  const rowDim = rowDims[0];
  const colDim = colDims[0];
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK }}>Gráficas</div>
          <div style={{ fontSize: 13, color: INK_SOFT, marginTop: 2 }}>
            {`Salen de la misma configuración: primer nivel de filas${
              rowDim ? ` (${DIMENSION_BY_KEY[rowDim].label})` : ''
            }${colDim ? ` por primer nivel de columnas (${DIMENSION_BY_KEY[colDim].label})` : ''}.`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: INK_SOFT }}>Medida</span>
          <Select
            label="Medida de las gráficas"
            labelHidden
            options={CHARTABLE.map((x) => ({ label: x.long, value: x.key }))}
            value={m}
            onChange={onMeasure}
          />
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: compare
            ? 'repeat(auto-fit, minmax(300px, 1fr))'
            : 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 24,
          marginTop: 16,
          borderTop: `1px solid ${LINE}`,
          paddingTop: 16,
        }}
      >
        <StackedBars pivot={pivot} m={m} fmt={fmt} rowDim={rowDim} colDim={colDim} tip={tip} />
        <Donut pivot={pivot} m={m} fmt={fmt} rowDim={rowDim} tip={tip} />
        {compare && (
          <DeltaBars
            pivot={pivot}
            m={m}
            fmt={fmt}
            colDim={colDim}
            rowDim={rowDim}
            compareLabel={compareLabel}
            tip={tip}
          />
        )}
      </div>
      {tip.node}
    </Card>
  );
};
